import { Button, tokens } from "@blurb/codex-react";
import { createStyles, Image, Stack } from "@mantine/core";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RpiTextField } from "../../../components/RpiTextField";
import { ProductAttributesSchema } from "../../../hooks/useStandardProducts";
import { RpaDrawer } from "../../../rpi-core/drawer/RpaDrawer";
import { createRpiDrawerView, RpiDrawerProps } from "../../../rpi-core/drawer/RpiDrawer";
import { ATTRIBUTES, isSoftcover } from "../../customer-products/attributeHelpers";
import { getDisplayName } from "../../customer-products/attributeValueMappings";
import { shouldRetry } from "../../../utils/query.util";

const useDetailsStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    padding: tokens.spacing[6],
  },
});

export const OpenApiProductView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { id: stringId } = useParams();
    const id = React.useMemo(() => stringId && Number.parseInt(stringId), []);

    React.useEffect(() => {
      if (!stringId) onClose();
    }, [stringId]);

    const { data, isLoading, error } = GraphQL.useOpenApiProductQuery(
      { id: id || -1 },
      {
        enabled: !!stringId && opened,
        refetchOnMount: true,
        retry: shouldRetry(onClose),
      }
    );
    const openApiProduct = React.useMemo(() => data?.openapiProduct, [data]);

    return (
      <RpaDrawer title="View SKU Details" isLoading={isLoading} error={error} opened={opened} onClose={onClose}>
        {openApiProduct && <OpenApiProductInfo openApiProduct={openApiProduct} />}
      </RpaDrawer>
    );
  }
);

interface OpenApiProductInfoProps {
  openApiProduct: GraphQLQueryData<GraphQL.OpenApiProductQuery>;
}

const OpenApiProductInfo: React.FC<OpenApiProductInfoProps> = ({ openApiProduct }) => {
  const { classes } = useDetailsStyles();
  const navigate = useNavigate();

  const validatedAttributes = React.useMemo(() => {
    if (!openApiProduct.skuAttributes) return undefined;
    try {
      return ProductAttributesSchema.parse(openApiProduct.skuAttributes);
    } catch (error) {
      console.error("Failed to parse skuAttributes:", error);
      return undefined;
    }
  }, [openApiProduct.skuAttributes]);

  const getDisplayValue = (attributeKey: string, fallback = "-"): string => {
    const rawValue = validatedAttributes?.[attributeKey];
    if (rawValue === undefined) return fallback;
    const displayName = getDisplayName(attributeKey, String(rawValue));
    return displayName || "LOOKUP ERROR";
  };

  const productImageSrc = validatedAttributes
    ? isSoftcover(validatedAttributes)
      ? "/assets/images/book-covers/view_SC_product_details.png"
      : "/assets/images/book-covers/view_HC_product_details.png"
    : undefined;

  return (
    <>
      <Stack className={classes.body} spacing={16}>
        <RpiTextField copyable label="Manufacturing SKU">
          {openApiProduct.openapiSku}
        </RpiTextField>
        <RpiTextField label="Cover Type">{getDisplayValue(ATTRIBUTES.COVER_TYPE)}</RpiTextField>
        <RpiTextField label="Cover Finish">{getDisplayValue(ATTRIBUTES.COVER_FINISH)}</RpiTextField>
        <RpiTextField label="Orientation">{getDisplayValue(ATTRIBUTES.ORIENTATION)}</RpiTextField>
        <RpiTextField label="Size">{getDisplayValue(ATTRIBUTES.DIMENSIONS)}</RpiTextField>
        <RpiTextField label="Bind">{getDisplayValue(ATTRIBUTES.BIND_TYPE)}</RpiTextField>
        <RpiTextField label="Paper Type">{getDisplayValue(ATTRIBUTES.TEXT_STOCK)}</RpiTextField>
        {productImageSrc && <Image src={productImageSrc} width="100%" fit="contain" radius="sm" />}
      </Stack>
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button
            variant="outlined"
            intent="primary"
            size="large"
            fullWidth
            data-testid="editSkuProductDetailsButton"
            onClick={() => navigate(`../edit/${openApiProduct.id}`)}
          >
            Edit SKU
          </Button>
        </div>
      </RpaDrawer.Footer>
    </>
  );
};

const useSkuDrawerStyles = createStyles({
  root: {
    padding: `0 ${tokens.spacing[6]}`,
  },
});

export type OpenApiProductCreateViewProps = {
  isOpened: boolean;
  onClose: () => void;
};
export function OpenApiProductCreateView({
  isOpened,
  onClose,
  children,
}: React.PropsWithChildren<OpenApiProductCreateViewProps>) {
  const { classes } = useSkuDrawerStyles();
  return (
    <RpaDrawer title="Add New SKU" opened={isOpened} onClose={onClose} data-testid="addNewSkuDrawer">
      <Stack className={classes.root}>{children}</Stack>
    </RpaDrawer>
  );
}

export type OpenApiProductEditViewProps = {
  isOpened: boolean;
  onClose: () => void;
};
export function OpenApiProductEditView({
  isOpened,
  onClose,
  children,
}: React.PropsWithChildren<OpenApiProductEditViewProps>) {
  const { classes } = useSkuDrawerStyles();
  return (
    <RpaDrawer title="Edit SKU" opened={isOpened} onClose={onClose} data-testid="editSkuDrawer">
      <Stack className={classes.root}>{children}</Stack>
    </RpaDrawer>
  );
}
